import { CountryCode, Locale, LOCALES } from 'config/constants/locales';
import devNL from 'config/nl/dev';
import productionNL from 'config/nl/production';
import stagingNL from 'config/nl/staging';
import { Carrier } from 'constants/carrier';
import { COMMON_PAYMENT_METHODS, PaymentMethod } from 'constants/payment-method';
import { GLOBAL_DATA_ENTRY_IDS } from 'config/contentful';
import { PdEnv } from 'constants/pd-env';
import { Region } from 'constants/shop';
import omit from 'lodash/omit';
import { COMMON_COLLECTION_HANDLES } from './collections/common-handles';
import { resolveHandles } from './collections/utils/resolve-handles';
import { buildShopDomain } from './constants/shop-domain';
import devCH from './de-CH/dev';
import { featureFlagsCH } from './de-CH/feature-flags';
import productionCH from './de-CH/production';
import stagingCH from './de-CH/staging';
import { COMMON_PAGE_PATHS_BY_REGION } from './common-page-paths';
import devDE from './de/dev';
import { featureFlagsDE } from './de/feature-flags';
import productionDE from './de/production';
import stagingDE from './de/staging';
import { featureFlagsNL } from './nl/feature-flags';
import { CommonCollections, Config, ShopEnvironment, ShopSettings } from './types';

const regions: Config['regions'] = {
  [Region.DE]: {
    dev: devDE,
    production: productionDE,
    staging: stagingDE,

    topLevelDomain: 'de',
    canonicalRegion: Region.DE,
    locale: LOCALES.DE,
    featureFlags: featureFlagsDE,
    carriers: [Carrier.DPD, Carrier.DHL, Carrier.DHL_GO_GREEN],
    shippingCountries: [CountryCode.DE, CountryCode.AT],
    paymentMethods: [...COMMON_PAYMENT_METHODS, PaymentMethod.KLARNA],
    shippingTime: '2-3',
    calendlyUrl:
      'https://calendly.com/futterberatungpetsdeli/telefonische-futterberatung-von-pets-deli',
    commonPagePaths: COMMON_PAGE_PATHS_BY_REGION[Region.DE],
    customerHotline: '4930220564405',
    globalDataEntryId: GLOBAL_DATA_ENTRY_IDS.de,
    helpUrl: 'https://help.petsdeli.de/hc/de/requests/new',
    magazineBasePath: 'magazin',
    minimumBasketValue: 2000,
    minimumBasketValueAT: 3000, // TODO: this is a workaround instead use minimumBasketValue: {de: number, at: number}
    minimumBasketValueBarfAT: 4000, // TODO: this is a workaround instead use minimumBasketValue: {de: number, at: number}
    minimumBasketValueFixedCoupon: 100,
    minimumSubscriptionValue: 2000,
    minimumSubscriptionValueAT: 3000, // TODO: this is a workaround instead use minimumBasketValue: {de: number, at: number}
    referalCouponAmountFriend: 10,
    referalCouponAmountSelf: 40,
    sovendusTrafficMediumNumber: '4',
    sovendusTrafficSourceNumber: '2294',
    subscriptionDiscount: 10,
    whatsappNumber: '493033083819',
    withdrawalFormUrl: 'https://help.petsdeli.de/hc/de/requests/new?ticket_form_id=360003621900',
  },
  [Region.CH]: {
    dev: devCH,
    production: productionCH,
    staging: stagingCH,

    topLevelDomain: 'ch',
    canonicalRegion: Region.DE,
    locale: LOCALES.CH,
    featureFlags: featureFlagsCH,
    carriers: [Carrier.DIE_POST],
    shippingCountries: [CountryCode.CH],
    paymentMethods: [...COMMON_PAYMENT_METHODS, PaymentMethod.TWINT],
    shippingTime: '2',
    // Switzerland has the same paths
    calendlyUrl:
      'https://calendly.com/futterberatungpetsdeli/telefonische-futterberatung-von-pets-deli',
    commonPagePaths: COMMON_PAGE_PATHS_BY_REGION[Region.CH],
    customerHotline: '41445051160',
    globalDataEntryId: GLOBAL_DATA_ENTRY_IDS['de-CH'],
    helpUrl: 'https://help.petsdeli.de/hc/de-ch/requests/new',
    magazineBasePath: 'magazin',
    minimumBasketValue: 3000,
    minimumBasketValueFixedCoupon: 100,
    minimumSubscriptionValue: 3000,
    referalCouponAmountFriend: 10,
    referalCouponAmountSelf: 40,
    sovendusTrafficMediumNumber: '1',
    sovendusTrafficSourceNumber: '7806',
    subscriptionDiscount: 10,
    whatsappNumber: '493033083819',
  },
  [Region.NL]: {
    dev: devNL,
    production: productionNL,
    staging: stagingNL,

    topLevelDomain: 'nl',
    canonicalRegion: Region.NL,
    locale: LOCALES.NL,
    featureFlags: featureFlagsNL,
    carriers: [Carrier.DHL, Carrier.DHL_GO_GREEN],
    shippingCountries: [CountryCode.NL],
    paymentMethods: [...COMMON_PAYMENT_METHODS, PaymentMethod.IDEAL],
    shippingTime: '2-3',
    calendlyUrl:
      'https://calendly.com/futterberatungpetsdeli/telefonische-futterberatung-von-pets-deli',
    commonPagePaths: COMMON_PAGE_PATHS_BY_REGION[Region.NL],
    customerHotline: '493082684420',
    globalDataEntryId: GLOBAL_DATA_ENTRY_IDS.nl,
    helpUrl: 'https://help.petsdeli.de/hc/de/requests/new',
    magazineBasePath: 'magazin',
    minimumBasketValue: 2000,
    minimumBasketValueFixedCoupon: 100,
    minimumSubscriptionValue: 2000,
    referalCouponAmountFriend: 10,
    referalCouponAmountSelf: 40,
    sovendusTrafficMediumNumber: null,
    sovendusTrafficSourceNumber: null,
    subscriptionDiscount: 10,
    whatsappNumber: '493033083819',
  },
};

const internalDomainsZones = Object.values(regions).map(({ topLevelDomain }) => topLevelDomain);
const internalDomains = internalDomainsZones.map((domainZone) => `petsdeli.${domainZone}`);
const trustedRedirectDomains = [
  ...internalDomainsZones.flatMap((domainZone) => [
    `petsdeli.${domainZone}`,
    `www.petsdeli.${domainZone}`,
  ]),
  'affiliate.petsdeli.de',
] as const;

const config: Config = {
  backendEnv: process.env.BACKEND_ENV,
  branchName: process.env.BRANCH_NAME,
  commitHash: process.env.COMMIT_HASH,
  disableSentry: process.env.DISABLE_SENTRY,
  isLive: process.env.IS_LIVE,
  // Supported locales for magzaine sitemap (e.g. Shops where we have the magazine)
  magazineSitemapLocales: [LOCALES.DE, LOCALES.CH, LOCALES.EN, LOCALES.NL],
  internalDomainsZones,
  internalDomains,
  trustedRedirectDomains,
  nodeEnv: process.env.NODE_ENV,
  pdEnv: process.env.PD_ENV,
  sentryUrl: 'https://1f4bffcbbc934ebea3a7cc168fa6420b@sentry.io/5168631',

  legalName: 'Pets Deli Tonius GmbH',
  supportEmail: 'office@petsdeli.de',
  regions,
};

interface ExtraConfigFields {
  region: Region;
  locale: Locale;
  commonCollections: CommonCollections;
  shopDomain: string;
  frontendUrl: string;
}

export type RegionConfig = ShopSettings &
  ShopEnvironment &
  Pick<Config, 'legalName' | 'supportEmail'> &
  ExtraConfigFields;

const REGION_BY_LOCALE: Record<Locale, Region> = {
  [LOCALES.DE]: Region.DE,
  [LOCALES.CH]: Region.CH,
  [LOCALES.EN]: Region.DE,
  [LOCALES.NL]: Region.NL,
};

// works as a model for config based on region
const getRegionConfig = (region: Region = Region.DE): RegionConfig => {
  const regionConfig = config.regions[region];
  const shopDomain = buildShopDomain(regionConfig.topLevelDomain);

  // Use dev config when BACKEND_ENV is 'local', otherwise use PD_ENV
  const envKey =
    process.env.BACKEND_ENV === 'local' || process.env.PD_ENV === PdEnv.DEVELOPMENT
      ? 'dev'
      : process.env.PD_ENV || PdEnv.PRODUCTION;
  return {
    ...regionConfig[envKey],
    ...omit(regionConfig, [PdEnv.PRODUCTION, PdEnv.STAGING, 'dev']), // TODO: can be simplified
    region,
    locale: regionConfig.locale,
    commonCollections: resolveHandles(COMMON_COLLECTION_HANDLES, region),
    shopDomain,
    get frontendUrl() {
      return `https://${shopDomain}`;
    },
    legalName: config.legalName,
    supportEmail: config.supportEmail,
    ...(process.env.CONTENTFUL_ENV ? { contentfulEnv: process.env.CONTENTFUL_ENV } : {}),
  };
};

const getLocaleConfig = (locale: Locale = LOCALES.DE): RegionConfig =>
  getRegionConfig(REGION_BY_LOCALE[locale]);

const getCanonicalConfig = (region: Region = Region.DE, locale?: Locale): RegionConfig => {
  const sourceConfig = locale ? getLocaleConfig(locale) : getRegionConfig(region);

  return getRegionConfig(sourceConfig.canonicalRegion);
};

export { config, getCanonicalConfig, getLocaleConfig, getRegionConfig };
